import { useEffect, useState, type FC, type ReactNode } from "react";
import PropTypes from "prop-types";
import useMediaQuery from "@mui/material/useMediaQuery";
import type { Theme } from "@mui/material/styles/createTheme";
import { styled } from "@mui/material/styles";
import { useSearchParams } from "react-router-dom";
import type { NavColor } from "src/types/settings";
import type { Section } from "../config";
import { MobileNav } from "../mobile-nav";
import { SideNav } from "./side-nav";
import { FreeTrialBanner } from "./free-trial-banner";
import { DirectChargeArrearsBanner } from "src/sections/dashboard/direct-charge-arrears-banner";
import { TopNav } from "./top-nav";
import { useMobileNav } from "./use-mobile-nav";
import { useSections } from "../config";
import useUserDataProvider from "src/contexts/snug/userData-context";
import { useTransactions } from "src/hooks/use-transactions";
import { PopupModal } from "react-calendly";
import { useDialog } from "src/hooks/use-dialog";
import { ProductDemoPlayer } from "src/sections/dashboard/overview/product-demo-player";

import Intercom from '@intercom/messenger-js-sdk';
import { DemoRouterDialog } from "src/sections/dashboard/overview/demo-router-dialog";

const SIDE_NAV_WIDTH: number = 280;


const VerticalLayoutRoot = styled("div")(({ theme }) => ({
  display: "flex",
  flex: "1 1 auto",
  maxWidth: "100%",
  [theme.breakpoints.up("lg")]: {
    paddingLeft: SIDE_NAV_WIDTH,
  },
}));

const VerticalLayoutContainer = styled("div")({
  display: "flex",
  flex: "1 1 auto",
  flexDirection: "column",
  width: "100%",
});

interface VerticalLayoutProps {
  children?: ReactNode;
  navColor?: NavColor;
  sections?: Section[];
}

export const VerticalLayout: FC<VerticalLayoutProps> = (props) => {
  const { children, navColor } = props;
  const sections = useSections();
  const lgUp = useMediaQuery((theme: Theme) => theme.breakpoints.up("lg"));
  const mobileNav = useMobileNav();
  const { professionalGroup, userData, isFirmAdmin, isPaidTier } =
    useUserDataProvider();
  
  // Get available credits using the useTransactions hook
  const { availableCredits } = useTransactions(isFirmAdmin);

  const [demoIsOpen, setDemoIsOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const productDemoDialog = useDialog();
  const playerDialog = useDialog();

  // Check for query parameter to auto-open Calendly modal
  useEffect(() => {
    const calendlyDemo = searchParams.get('calendly_demo');
    if (calendlyDemo === 'true') {
      setDemoIsOpen(true);
      // Remove the query parameter after opening the modal
      searchParams.delete('calendly_demo');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    const showDemoPopup =
      professionalGroup?.show_demo_popup &&
      !isPaidTier;
    const demoPopupShown = sessionStorage.getItem("demoPopupShown");
    if (showDemoPopup && !demoPopupShown) {
      setDemoIsOpen(true);
      sessionStorage.setItem("demoPopupShown", "true");
    }
  }, [isPaidTier, professionalGroup]);

  useEffect(() => {
    const showDemoDefaultPopup = !isPaidTier;
    const demoDefaultPopupShown = sessionStorage.getItem(
      "demoDefaultPopupShown"
    );
    if (showDemoDefaultPopup && !demoDefaultPopupShown) {
      playerDialog.handleOpen();
      sessionStorage.setItem("demoDefaultPopupShown", "true");
    }
  }, [isPaidTier, playerDialog]);

  useEffect(() => {
    console.log('Looking for intercom...')
    if ((professionalGroup || userData) && !professionalGroup?.dashboard_wl_on && professionalGroup?.onboarding_payment_date) {
      console.log('Found intercom...')
      const intercomSettings: any = {
        app_id: 'spf5tsws',
        user_id: userData?.ud_id || 'NA',
        name: userData?.full_name,
        email: userData?.contact_email,
        created_at: userData?.created_at
          ? Math.floor(new Date(userData.created_at).getTime() / 1000)
          : 0,
        hide_default_launcher: false
      };
      // White-labeled agents get a custom messenger style; everyone else falls
      // back to the Intercom default by omitting the field entirely.
      // if (
      //   professionalGroup?.dashboard_wl_on &&
      //   userData?.professional_group_role_user_data?.role === "PRO_GROUP_AGENT"
      // ) {
      //   intercomSettings.messenger_style_id = 2890326;
      // }
      Intercom(intercomSettings);
    }
  }, [userData, professionalGroup]);

  return (
    <>
      <TopNav
        onMobileNavOpen={mobileNav.handleOpen}
        availableCredits={availableCredits?.available_credit || 0}
        showCredits={isFirmAdmin}
      />
      {lgUp && (
        <SideNav
          color={navColor}
          sections={sections}
          
        />
      )}
      {!lgUp && (
        <MobileNav
          color={navColor}
          onClose={mobileNav.handleClose}
          open={mobileNav.open}
          sections={sections}
          
        />
      )}
      <VerticalLayoutRoot sx={{bgcolor: '#fcfcfc'}}>
        <VerticalLayoutContainer>
          {false && (!isPaidTier ? <FreeTrialBanner /> :
          !professionalGroup?.onboarding_completed_at ? <FreeTrialBanner /> :
          <></>)}
          <DirectChargeArrearsBanner />
          {children}
        </VerticalLayoutContainer>
      </VerticalLayoutRoot>
      <PopupModal
        url={`https://calendly.com/financial-professionals-snug/snug-estate-planning-demo-pilot?hide_gdpr_banner=1&utm_source=dashboard&utm_content=${userData?.professional_group_role_user_data?.id}`}
        onModalClose={() => setDemoIsOpen(false)}
        open={demoIsOpen}
        prefill={{
          email: userData?.contact_email,
          name: userData?.full_name,
        }}
        /*
         * react-calendly uses React's Portal feature (https://reactjs.org/docs/portals.html) to render the popup modal. As a result, you'll need to
         * specify the rootElement property to ensure that the modal is inserted into the correct domNode.
         */
        rootElement={document.getElementById("root")!}
      />
      <ProductDemoPlayer
        onClose={productDemoDialog.handleClose}
        open={productDemoDialog.open}
        openCalendly={setDemoIsOpen}
      />
      <DemoRouterDialog
        onClose={playerDialog.handleClose}
        open={playerDialog.open}
        openCalendly={setDemoIsOpen}
      />
    </>
  );
};

VerticalLayout.propTypes = {
  children: PropTypes.node,
  navColor: PropTypes.oneOf<NavColor>(["blend-in", "discreet", "evident"]),
  sections: PropTypes.array,
};
